import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getProtocolsGroupedByBusinessLine,
  getProtocolById,
  createProtocol,
  saveProtocol,
  getBusinessLines,
} from '../../../../services/protocolService';
import ProtocolCreateEditModal from './ProtocolCreateEditModal';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import ProtocolViewModal from './ProtocolViewModal';
import PromptVariantsPanel from './PromptVariantsPanel';
import './ServiceProtocol.css';
import usePermissionsConfiguration from 'permissions/modules/configuration/usePermissionsConfiguration';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { serviceProtocolI18n } from './i18n';
import { useSnackbar } from '../../../../context/SnackbarContext';

const DEFAULT_GROUP_PAGE_SIZE = 10;

export default function ServiceProtocol() {
  const { language } = useLanguage();
  const serviceProtocolLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: serviceProtocolI18n });
  const { showSnackbar } = useSnackbar();
  // 1) Hooks first
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Input, Table, Badge, IconButton, Modal, Dropdown } =
    DesignSystemComponents ?? {};

  const [groups, setGroups] = useState([]); // [{ id, lineName, protocols: [...] }]
  const [expanded, setExpanded] = useState(() => ({})); // { [groupId]: bool }
  const [search, setSearch] = useState('');
  const [totals, setTotals] = useState({
    protocolsEnabled: 0,
    protocolsDisabled: 0,
    businessLines: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [businessLines, setBusinessLines] = useState([]); // Líneas de negocio con IDs del backend
  const [groupPagination, setGroupPagination] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [mode, setMode] = useState('create');
  const [showDelete, setShowDelete] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [showView, setShowView] = useState(false);
  const [viewProtocol, setViewProtocol] = useState(null);
  const [activeView, setActiveView] = useState('protocols'); // 'protocols' | 'prompts'
  const protocolRefs = useRef({});
  const location = useLocation();
  const protocolIdToOpen =
    location.state?.protocolIdToOpen ?? location.state?.protocolId ?? null;
  const protocolScrollRef = useRef({ id: null, done: false });

  useEffect(() => {
    serviceProtocolI18n.changeLanguage(serviceProtocolLanguage);
  }, [serviceProtocolLanguage]);

  const {
    canCreateProtocol,
    canViewProtocol,
    canEditProtocol,
    canDeleteProtocol,
    canAccessPrompts,
  } = usePermissionsConfiguration();

  useEffect(() => {
    let mounted = true;

    const fetchProtocols = async () => {
      try {
        setLoading(true);
        const response = await getProtocolsGroupedByBusinessLine();

        if (!mounted) return;

        // Guardar los totales
        setTotals(response.totals);

        // Mapear los datos del backend al formato del frontend
        const mappedData = response.data.map((businessLine, index) => ({
          id: `ln-${index + 1}`,
          lineName: businessLine.businessLineName,
          protocols: businessLine.protocols.map((protocol, pIndex) => ({
            id: `${index + 1}-${pIndex + 1}`,
            protocolId: protocol.protocolId, // ✅ ID del protocolo del backend
            name: protocol.protocolName,
            steps: protocol.steps,
            creator: protocol.creatorName || null,
            status: protocol.isEnabled ? 'active' : 'inactive',
            isEnabled: protocol.isEnabled,
          })),
        }));

        setGroups(mappedData);
        setGroupPagination({});

        // Expandir el primer grupo con protocolos por defecto
        const firstGroupWithProtocols = mappedData.find(
          (g) => g.protocols.length > 0
        );
        if (firstGroupWithProtocols) {
          setExpanded((e) => ({ ...e, [firstGroupWithProtocols.id]: true }));
        }
      } catch (err) {
        if (!mounted) return;
        console.error('Error al cargar protocolos:', err);
        setError(t('service_protocol_error_fetch'));
        showSnackbar(
          err?.customMessage || t('service_protocol_error_fetch'),
          'error'
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchProtocols();

    return () => {
      mounted = false;
    };
  }, []);

  // Cargar líneas de negocio con IDs
  useEffect(() => {
    let mounted = true;

    const fetchBusinessLines = async () => {
      try {
        const lines = await getBusinessLines();
        if (!mounted) return;
        setBusinessLines(lines);
      } catch (err) {
        if (!mounted) return;
        console.error('Error al cargar líneas de negocio:', err);
      }
    };

    fetchBusinessLines();

    return () => {
      mounted = false;
    };
  }, []);

  // Calcular totales desde los datos reales del backend
  const { totalProtocols, activeProtocols, totalLines } = useMemo(() => {
    return {
      totalProtocols: totals.protocolsEnabled + totals.protocolsDisabled,
      activeProtocols: totals.protocolsEnabled,
      totalLines: totals.businessLines,
    };
  }, [totals]);

  // Search filter (by protocol name)
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        protocols: g.protocols.filter((p) => p.name.toLowerCase().includes(q)),
      }))
      .filter((g) => g.protocols.length > 0);
  }, [groups, search]);

  const toggleGroup = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const getGroupPagination = (groupId) => {
    const pagination = groupPagination[groupId];
    return {
      page: pagination?.page ?? 0,
      pageSize: pagination?.pageSize ?? DEFAULT_GROUP_PAGE_SIZE,
    };
  };

  const handleGroupPageChange = (groupId, newPage) => {
    setGroupPagination((prev) => ({
      ...prev,
      [groupId]: {
        ...(prev[groupId] || {}),
        page: newPage,
        pageSize: prev[groupId]?.pageSize ?? DEFAULT_GROUP_PAGE_SIZE,
      },
    }));
  };

  const handleGroupPageSizeChange = (groupId, newSize) => {
    setGroupPagination((prev) => ({
      ...prev,
      [groupId]: {
        page: 0,
        pageSize: newSize,
      },
    }));
  };

  useEffect(() => {
    if (!protocolIdToOpen || !groups.length) return;
    const protocolIdStr = String(protocolIdToOpen);
    if (
      protocolScrollRef.current.done &&
      protocolScrollRef.current.id === protocolIdStr
    ) {
      return;
    }

    const match = groups.reduce(
      (acc, group) => {
        const index = group.protocols.findIndex(
          (p) => String(p.protocolId) === protocolIdStr
        );
        if (index >= 0) {
          return { group, index };
        }
        return acc;
      },
      { group: null, index: -1 }
    );

    if (!match.group) return;

    setExpanded((prev) => ({ ...prev, [match.group.id]: true }));
    setGroupPagination((prev) => {
      const pageSize =
        prev[match.group.id]?.pageSize ?? DEFAULT_GROUP_PAGE_SIZE;
      const page = Math.floor(match.index / pageSize);
      return {
        ...prev,
        [match.group.id]: {
          page,
          pageSize,
        },
      };
    });

    protocolScrollRef.current = { id: protocolIdStr, done: false };

    setTimeout(() => {
      const el = protocolRefs.current[protocolIdStr];
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const rowEl = el.closest('tr') ?? el;
      rowEl.classList.add('protocol-highlight-row');
      setTimeout(() => rowEl.classList.remove('protocol-highlight-row'), 1000);
      protocolScrollRef.current = { id: protocolIdStr, done: true };
    }, 250);
  }, [protocolIdToOpen, groups]);

  const onView = async (row, lineName) => {
    try {
      setLoading(true);

      const protocolId = row.protocolId;

      if (!protocolId) {
        throw new Error(t('service_protocol_error_protocol_id'));
      }

      // Obtener el protocolo completo desde el backend
      const protocolData = await getProtocolById(protocolId);

      // Mapear steps a {id, name} para que ProtocolViewModal los procese
      const mappedSteps = Array.isArray(protocolData.steps)
        ? protocolData.steps.map((step) => ({
            id: step.id,
            name: step.name,
          }))
        : [];

      // Mapear todo el protocolo con la información completa
      const mappedProtocol = {
        id: protocolData.id,
        name: protocolData.name,
        lineName: protocolData.businessLine?.name || lineName || '',
        businessLineName: protocolData.businessLine?.name || lineName || '',
        steps: mappedSteps,
        isEnabled: protocolData.isEnabled,
        status: protocolData.isEnabled ? 'active' : 'inactive',
        businessLines: protocolData.businessLines || [], // Array de todas las líneas disponibles
      };

      setViewProtocol(mappedProtocol);
      setShowView(true);
    } catch (error) {
      console.error('Error al cargar protocolo para visualización:', error);
      showSnackbar(
        error?.customMessage || t('service_protocol_error_view'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const onDelete = (row) => {
    setSelectedProtocol(row);
    setShowDelete(true);
  };
  const onCreate = () => {
    setMode('create');
    setEditData(null);
    setShowModal(true);
  };

  const onEdit = async (row) => {
    try {
      setLoading(true);

      const protocolId = row.protocolId;

      if (!protocolId) {
        throw new Error(t('service_protocol_error_protocol_id'));
      }

      // Obtener el protocolo completo desde el backend
      const protocolData = await getProtocolById(protocolId);

      // Mapear steps completos con id y name para el formulario
      const mappedSteps = Array.isArray(protocolData.steps)
        ? protocolData.steps.map((step) => ({
            id: step.id,
            name: step.name,
          }))
        : [{ id: 0, name: '' }];

      // Mapear datos para el modal de edición
      const mappedData = {
        id: protocolData.id,
        name: protocolData.name,
        line: protocolData.businessLine?.name || '',
        lineId: protocolData.businessLine?.id || null,
        steps: mappedSteps, // ✅ Ahora incluye id y name
        status: protocolData.isEnabled ? 'active' : 'inactive',
        businessLines: protocolData.businessLines || [],
      };

      setMode('edit');
      setEditData(mappedData);
      setShowModal(true);
    } catch (error) {
      console.error('Error al cargar protocolo para edición:', error);
      showSnackbar(
        error?.customMessage || t('service_protocol_error_edit'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (payload) => {
    try {
      setLoading(true);

      // Encontrar el businessLineId desde el nombre de la línea
      const businessLine = businessLines.find((bl) => bl.name === payload.line);

      if (!businessLine) {
        throw new Error(t('service_protocol_error_line_not_found'));
      }

      // Obtener usuarioCreadorId (ajusta según tu implementación de autenticación)
      // Por ahora asumo que está en localStorage o contexto
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const usuarioCreadorId = userData.id || 1; // Valor por defecto 1 si no hay usuario

      if (mode === 'create') {
        // CREAR NUEVO PROTOCOLO
        const createPayload = {
          name: payload.name,
          businessLineId: businessLine.id,
          isEnabled: payload.status === 'active',
          usuarioCreadorId: usuarioCreadorId,
          steps: payload.steps.map((step) => ({
            id: 0, // 0 para nuevos pasos
            name: step.name,
          })),
        };

        await createProtocol(createPayload);

        showSnackbar(t('service_protocol_success_created'), 'success');
      } else {
        // EDITAR PROTOCOLO EXISTENTE
        const savePayload = {
          id: payload.id,
          name: payload.name,
          businessLineId: businessLine.id,
          isEnabled: payload.status === 'active',
          steps: payload.steps.map((step) => ({
            id: step.id || 0, // Si tiene ID lo usa, sino 0 para nuevo paso
            name: step.name,
          })),
        };

        await saveProtocol(savePayload);

        showSnackbar(t('service_protocol_success_updated'), 'success');
      }

      // Recargar los protocolos después de guardar
      const protocolsResponse = await getProtocolsGroupedByBusinessLine();
      setTotals(protocolsResponse.totals);

      const mappedData = protocolsResponse.data.map((businessLine, index) => ({
        id: `ln-${index + 1}`,
        lineName: businessLine.businessLineName,
        protocols: businessLine.protocols.map((protocol, pIndex) => ({
          id: `${index + 1}-${pIndex + 1}`,
          protocolId: protocol.protocolId,
          name: protocol.protocolName,
          steps: protocol.steps,
          creator: protocol.creatorName || null,
          status: protocol.isEnabled ? 'active' : 'inactive',
          isEnabled: protocol.isEnabled,
        })),
      }));

      setGroups(mappedData);
      setShowModal(false);
    } catch (error) {
      console.error('❌ Error al guardar protocolo:', error);
      showSnackbar(
        error.response?.data?.message ||
          error?.customMessage ||
          t('service_protocol_error_save'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProtocol) return;
    try {
      // TODO: Llamar API de eliminación cuando esté disponible
      // await deleteProtocol(selectedProtocol.id);

      // Eliminar protocolo de la UI (estado local)
      setGroups((prev) =>
        prev.map((g) => ({
          ...g,
          protocols: g.protocols.filter((p) => p.id !== selectedProtocol.id),
        }))
      );

      // Actualizar totales de manera optimista
      setTotals((prev) => ({
        ...prev,
        protocolsEnabled: selectedProtocol.isEnabled
          ? Math.max(0, prev.protocolsEnabled - 1)
          : prev.protocolsEnabled,
        protocolsDisabled: !selectedProtocol.isEnabled
          ? Math.max(0, prev.protocolsDisabled - 1)
          : prev.protocolsDisabled,
      }));

      setShowDelete(false);
      setSelectedProtocol(null);
      showSnackbar(t('service_protocol_success_deleted'), 'success');
    } catch (error) {
      console.error('Error al eliminar protocolo:', error);
      showSnackbar(
        error?.customMessage || t('service_protocol_error_delete'),
        'error'
      );
    }
  };

  // 2) Fallback if DS not provided
  if (!Card || !Button || !Input || !Table || !Badge) return null;

  const statusColor = (s) => (s === 'active' ? 'success' : 'light');
  const statusLabel = (status) =>
    status === 'active'
      ? t('service_protocol_status_active')
      : t('service_protocol_status_inactive');

  // Mostrar estado de carga
  if (loading) {
    return (
      <Card specialPadding='p-4'>
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '400px' }}
        >
          <p>{t('service_protocol_loading')}</p>
        </div>
      </Card>
    );
  }

  // Mostrar error si existe
  if (error) {
    return (
      <Card specialPadding='p-4'>
        <div
          className='d-flex flex-column justify-content-center align-items-center'
          style={{ minHeight: '400px' }}
        >
          <p className='text-danger mb-3'>{error}</p>
          <Button
            label={t('service_protocol_retry')}
            size='sm'
            variant='primary'
            onClick={() => window.location.reload()}
          />
        </div>
      </Card>
    );
  }

  return (
    <Card specialPadding='p-4'>
      {activeView === 'prompts' ? (
        <PromptVariantsPanel
          Card={Card}
          Button={Button}
          Input={Input}
          Table={Table}
          Badge={Badge}
          IconButton={IconButton}
          Modal={Modal}
          onBack={() => setActiveView('protocols')}
        />
      ) : (
        <>
          {/* Header */}
          <div className='d-flex justify-content-between align-items-start mb-3'>
            <div>
              <h2 className='mb-1'>{t('service_protocol_title')}</h2>
              <p className='caption font-light'>
                {t('service_protocol_subtitle')}
              </p>
            </div>
            <div className='d-flex gap-2'>
              <Button
                label={t('service_protocol_prompts_button')}
                size='sm'
                variant='secondary'
                onClick={() => setActiveView('prompts')}
                disabled={!canAccessPrompts}
              />
              <Button
                label={t('service_protocol_new_button')}
                size='sm'
                variant='primary'
                iconStart='plusLg'
                onClick={onCreate}
                disabled={!canCreateProtocol}
              />
            </div>
          </div>
          {/* KPIs - Con datos reales del backend */}
          <div className='d-flex gap-3 mb-3'>
            <Card specialPadding='p-3' className='w-100'>
              <p className='mini font-light'>
                {t('service_protocol_kpi_total')}
              </p>
              <h2 className=''>{totalProtocols}</h2>
            </Card>
            <Card specialPadding='p-3' className='w-100'>
              <p className='mini font-light'>
                {t('service_protocol_kpi_active')}
              </p>
              <h2 className='color-success-500'>{activeProtocols}</h2>
            </Card>
            <Card specialPadding='p-3' className='w-100'>
              <p className='mini font-light'>
                {t('service_protocol_kpi_lines')}
              </p>
              <h2 className='color-primary-500'>{totalLines}</h2>
            </Card>
          </div>
          <div
            className='p-3'
            style={{
              background: '#fff',
              border: '1px solid #e6e8eb',
              borderRadius: 12,
            }}
          >
            <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-3'>
              <h3 className='h6 m-0'>{t('service_protocol_group_title')}</h3>
              <div style={{ minWidth: 280, flex: '0 0 280px' }}>
                <Input
                  icon='search'
                  name='searchProtocol'
                  placeholder={t('service_protocol_search_placeholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Accordion + nested tables */}
            <div className='d-flex flex-column gap-2'>
              {filteredGroups.map((group) => {
                const isOpen = !!expanded[group.id];
                const count = group.protocols.length;
                const pagination = getGroupPagination(group.id);
                return (
                  <div
                    key={group.id}
                    style={{
                      border: '1px solid #e6e8eb',
                      borderRadius: 12,
                      overflow: 'hidden',
                      background: '#f8fafc',
                    }}
                  >
                    {/* Group header */}
                    <button
                      type='button'
                      onClick={() => toggleGroup(group.id)}
                      className='w-100 d-flex align-items-center justify-content-between'
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: '12px 16px',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      aria-expanded={isOpen}
                    >
                      <div className='d-flex align-items-center gap-2'>
                        <IconButton
                          icon={isOpen ? 'chevronDown' : 'chevronRight'}
                          ariaLabel={t('service_protocol_toggle_aria')}
                          size='sm'
                        />
                        <IconButton
                          icon='phone'
                          ariaLabel={t('service_protocol_category_aria')}
                          size='sm'
                        />
                        <span className='fw-medium'>{group.lineName}</span>
                        <Badge
                          text={
                            count === 1
                              ? t('service_protocol_badge_one', { count })
                              : t('service_protocol_badge_many', { count })
                          }
                          color='primary'
                          size='sm'
                        />
                      </div>
                    </button>

                    {/* Nested table */}
                    {isOpen && (
                      <div
                        className='protocol-table-wrap'
                        style={{
                          background: '#fff',
                          borderTop: '1px solid #e6e8eb',
                        }}
                      >
                        <Table
                          columns={[
                            {
                              accessor: 'name',
                              label: t('service_protocol_col_name'),
                              render: (row) => (
                                <div
                                  className='d-flex align-items-center gap-2'
                                  ref={(el) => {
                                    if (el) {
                                      protocolRefs.current[
                                        String(row.protocolId)
                                      ] = el;
                                    }
                                  }}
                                  data-protocol-id={row.protocolId}
                                >
                                  <IconButton
                                    icon='listTask'
                                    ariaLabel={t('service_protocol_col_steps')}
                                    size='sm'
                                  />
                                  <span>{row.name}</span>
                                </div>
                              ),
                            },
                            {
                              accessor: 'steps',
                              label: t('service_protocol_col_steps'),
                              render: (row) => (
                                <Badge
                                  text={t('service_protocol_steps_badge', {
                                    count: row.steps,
                                  })}
                                  color='info'
                                  size='sm'
                                />
                              ),
                            },
                            {
                              accessor: 'creator',
                              label: t('service_protocol_col_creator'),
                              render: (row) =>
                                row.creator ||
                                t('service_protocol_creator_fallback'),
                            },
                            {
                              accessor: 'status',
                              label: t('service_protocol_col_status'),
                              render: (row) => (
                                <Badge
                                  text={statusLabel(row.status)}
                                  color={statusColor(row.status)}
                                  size='sm'
                                />
                              ),
                            },
                            {
                              label: t('service_protocol_col_actions'),
                              render: (row) => (
                                <div className='d-flex align-items-center gap-2'>
                                  <IconButton
                                    ariaLabel={t(
                                      'service_protocol_action_view'
                                    )}
                                    icon='eye'
                                    onClick={() => onView(row, group.lineName)}
                                    size='md'
                                    disabled={!canViewProtocol}
                                  />
                                  <IconButton
                                    ariaLabel={t(
                                      'service_protocol_action_edit'
                                    )}
                                    icon='pencil'
                                    onClick={() => onEdit(row)}
                                    size='md'
                                    iconStroke='var(--primary-500)'
                                    disabled={!canEditProtocol}
                                  />
                                  <IconButton
                                    ariaLabel={t(
                                      'service_protocol_action_delete'
                                    )}
                                    icon='trash'
                                    onClick={() => onDelete(row)}
                                    size='md'
                                    iconStroke='var(--error-500)'
                                    disabled={!canDeleteProtocol}
                                  />
                                </div>
                              ),
                            },
                          ]}
                          data={group.protocols}
                          page={pagination.page}
                          onPageChange={(newPage) =>
                            handleGroupPageChange(group.id, newPage)
                          }
                          pageSize={pagination.pageSize}
                          onPageSizeChange={(newSize) =>
                            handleGroupPageSizeChange(group.id, newSize)
                          }
                          totalItems={group.protocols.length}
                          paginator
                          loading={loading}
                          paginatorGroup={[
                            { label: '10', value: 10 },
                            { label: '20', value: 20 },
                            { label: '50', value: 50 },
                            { label: '100', value: 100 },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <ProtocolCreateEditModal
            Modal={Modal}
            Input={Input}
            Dropdown={Dropdown}
            Button={Button}
            Badge={Badge}
            IconButton={IconButton}
            show={showModal}
            mode={mode}
            initialData={editData}
            lineOptions={businessLines.map((bl) => ({
              label: bl.name,
              value: bl.name,
              id: bl.id,
            }))}
            onSave={onSave}
            onClose={() => setShowModal(false)}
          />
          <ConfirmModal
            Modal={Modal}
            show={showDelete}
            title={t('service_protocol_confirm_delete_title')}
            message={
              selectedProtocol
                ? t('service_protocol_confirm_delete_message', {
                    name: selectedProtocol.name,
                  })
                : t('service_protocol_confirm_delete_fallback')
            }
            confirmLabel={t('service_protocol_confirm_delete_button')}
            confirmVariant='danger'
            onConfirm={handleDelete}
            onClose={() => setShowDelete(false)}
          />
          <ProtocolViewModal
            Modal={Modal}
            Badge={Badge}
            show={showView}
            protocol={viewProtocol}
            onClose={() => {
              setShowView(false);
              setViewProtocol(null);
            }}
          />
        </>
      )}
    </Card>
  );
}
